import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Eye, Lock, Users, Clock, FileText, Phone, Globe, Settings, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  const { t, translations } = useLanguage();

  const handleClose = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  const handleOpen = () => {
    document.body.style.overflow = 'hidden';
  };

  React.useEffect(() => {
    if (isOpen) {
      handleOpen();
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sections = [
    {
      id: 'intro',
      icon: Shield,
      title: t('privacy.sections.intro.title'),
      content: translations.privacy?.sections?.intro?.content
    },
    {
      id: 'dataCollection',
      icon: Eye,
      title: t('privacy.sections.dataCollection.title'),
      content: translations.privacy?.sections?.dataCollection?.content
    },
    {
      id: 'dataUsage',
      icon: Settings,
      title: t('privacy.sections.dataUsage.title'),
      content: translations.privacy?.sections?.dataUsage?.content
    },
    {
      id: 'legalBasis',
      icon: FileText,
      title: t('privacy.sections.legalBasis.title'),
      content: translations.privacy?.sections?.legalBasis?.content
    },
    {
      id: 'dataSharing',
      icon: Users,
      title: t('privacy.sections.dataSharing.title'),
      content: translations.privacy?.sections?.dataSharing?.content
    },
    {
      id: 'dataRetention',
      icon: Clock,
      title: t('privacy.sections.dataRetention.title'),
      content: translations.privacy?.sections?.dataRetention?.content
    },
    {
      id: 'rights',
      icon: Lock,
      title: t('privacy.sections.rights.title'),
      content: translations.privacy?.sections?.rights?.content
    },
    {
      id: 'security',
      icon: Shield,
      title: t('privacy.sections.security.title'),
      content: translations.privacy?.sections?.security?.content
    },
    {
      id: 'cookies',
      icon: Globe,
      title: t('privacy.sections.cookies.title'),
      content: translations.privacy?.sections?.cookies?.content
    },
    {
      id: 'thirdParties',
      icon: Users,
      title: t('privacy.sections.thirdParties.title'),
      content: translations.privacy?.sections?.thirdParties?.content
    },
    {
      id: 'updates',
      icon: AlertCircle,
      title: t('privacy.sections.updates.title'),
      content: translations.privacy?.sections?.updates?.content
    },
    {
      id: 'contact',
      icon: Phone,
      title: t('privacy.sections.contact.title'),
      content: translations.privacy?.sections?.contact?.content
    }
  ];

  const renderSectionContent = (content: any) => {
    if (typeof content === 'string') {
      return <p className="text-gray-700 leading-relaxed">{content}</p>;
    }
    
    if (content && typeof content === 'object') {
      return (
        <div className="space-y-4">
          {content.intro && (
            <p className="text-gray-700 leading-relaxed font-medium">{content.intro}</p>
          )}
          {content.items && Array.isArray(content.items) && (
            <ul className="space-y-2 ml-4">
              {content.items.map((item: string, index: number) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          )}
          {content.exercise && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
              <p className="text-blue-800 font-medium">{content.exercise}</p>
            </div>
          )}
          {content.note && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <p className="text-yellow-800 text-sm"><strong>Note :</strong> {content.note}</p>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">{t('privacy.title')}</h2>
              </div>
              <motion.button
                onClick={handleClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-8">
                {sections.map((section, index) => {
                  const IconComponent = section.icon;
                  
                  return (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="bg-gray-50 rounded-xl p-6"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                      </div>
                      
                      {renderSectionContent(section.content)}
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="text-center mt-8 pt-6 border-t border-gray-200"
              >
                <div className="text-sm text-gray-500 space-y-1">
                  <p>© {new Date().getFullYear()} Autocat S.à r.l. - Conformément au RGPD</p>
                  <p>Dernière mise à jour : {new Date().toLocaleDateString()}</p>
                  <p className="font-medium">
                    Contact DPO : <a href="mailto:privacy@autocat.lu" className="text-blue-600 hover:text-blue-700">privacy@autocat.lu</a>
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyModal;